/**
 * Standalone Adapter for TagadaPay SDK v2
 *
 * Provides a vanilla JS interface for using the SDK without a framework.
 * Suitable for standard HTML/JS sites or other frameworks (Svelte, Angular, etc).
 *
 * This layer is a very thin wrapper over the Core SDK.
 */

import { TagadaClient, TagadaClientConfig, TagadaState } from '../core/client';
import { ApiClient } from '../core/resources/apiClient';
import { CheckoutResource } from '../core/resources/checkout';
import { getAssignedStepConfig, getAssignedPaymentFlowId } from '../core/funnelClient';
import { detectEnvironment } from '../core/config/environment';
import { getBasisTheoryApiKey as _getBtApiKey, getBasisTheoryTenantId as _getBtTenantId } from '../../react/config/payment';

// ============================================================================
// AUTO SCRIPT INJECTOR
// ============================================================================
// Automatically injects and executes scripts from stepConfig when SDK loads
// This runs for all standalone SDK users (not just React apps)

interface StepConfigScript {
    name: string;
    enabled: boolean;
    content: string;
    position?: 'head-start' | 'head-end' | 'body-start' | 'body-end';
}

/**
 * Parse step config from window variable or meta tag
 */
function parseStepConfigScripts(): StepConfigScript[] {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return [];
    }

    // Try to get stepConfig (uses same logic as getAssignedStepConfig)
    const stepConfig = getAssignedStepConfig();
    if (stepConfig?.scripts) {
        return stepConfig.scripts.filter(s => s.enabled);
    }

    return [];
}

/**
 * Inject a script at the specified position
 */
function injectScript(script: StepConfigScript, index: number): void {
    const position = script.position || 'body-end';
    const scriptId = `tagada-stepconfig-script-${index}`;

    // Skip if already injected
    if (document.getElementById(scriptId)) {
        return;
    }

    // Extract script content (remove <script> tags if present)
    let scriptBody = script.content.trim();
    const scriptTagMatch = scriptBody.match(/^<script[^>]*>([\s\S]*)<\/script>$/i);
    if (scriptTagMatch) {
        scriptBody = scriptTagMatch[1].trim();
    }

    if (!scriptBody) return;

    // Wrap script content with error handling
    // NOTE: Use string concatenation instead of template literals to avoid breaking
    // when scriptBody contains backticks or ${...} expressions
    const wrappedScript = '(function() {\n' +
        '  try {\n' +
        '    // Script: ' + script.name + '\n' +
        scriptBody + '\n' +
        '  } catch (error) {\n' +
        '    console.error("[TagadaPay] StepConfig script error:", error);\n' +
        '  }\n' +
        '})();';

    // Create script element
    const scriptElement = document.createElement('script');
    scriptElement.id = scriptId;
    scriptElement.setAttribute('data-tagada-stepconfig-script', 'true');
    scriptElement.setAttribute('data-script-name', script.name);
    scriptElement.textContent = wrappedScript;

    // Inject at the correct position
    switch (position) {
        case 'head-start':
            if (document.head.firstChild) {
                document.head.insertBefore(scriptElement, document.head.firstChild);
            } else {
                document.head.appendChild(scriptElement);
            }
            break;
        case 'head-end':
            document.head.appendChild(scriptElement);
            break;
        case 'body-start':
            if (document.body.firstChild) {
                document.body.insertBefore(scriptElement, document.body.firstChild);
            } else {
                document.body.appendChild(scriptElement);
            }
            break;
        case 'body-end':
        default:
            document.body.appendChild(scriptElement);
            break;
    }
}

/**
 * Auto-inject all stepConfig scripts
 * Called automatically when SDK loads
 * Can also be called manually if needed (e.g., after dynamic config update)
 */
export function injectStepConfigScripts(): void {
    const scripts = parseStepConfigScripts();
    if (scripts.length === 0) return;

    scripts.forEach((script, index) => {
        injectScript(script, index);
    });
}

// Auto-run script injection when SDK loads
// Uses requestIdleCallback or setTimeout fallback for non-blocking execution
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const runInjection = () => {
        // Wait for body to be available
        if (document.body) {
            injectStepConfigScripts();
        } else {
            // Body not ready, wait for DOMContentLoaded
            document.addEventListener('DOMContentLoaded', injectStepConfigScripts, { once: true });
        }
    };

    // Run after current script execution completes
    if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(runInjection, { timeout: 100 });
    } else {
        setTimeout(runInjection, 0);
    }
}

/**
 * Factory function to create a Tagada Client instance.
 * Features (like funnel) can be toggled via the config.
 */
export function createTagadaClient(config: TagadaClientConfig = {}): TagadaClient {
    return new TagadaClient(config);
}

// Re-export Core Classes
export { TagadaClient, ApiClient, CheckoutResource };

// Re-export Resource Classes (for standalone resolvers / builders)
export { ShippingRatesResource } from '../core/resources/shippingRates';
export { PaymentsResource } from '../core/resources/payments';
export { OrdersResource } from '../core/resources/orders';
export { OffersResource } from '../core/resources/offers';
export { ExpressPaymentMethodsResource } from '../core/resources/expressPaymentMethods';
export type { PaymentMethod as ExpressPaymentMethod } from '../core/resources/expressPaymentMethods';
export type { ShippingRate, ShippingRatesResponse, ShippingRatesPreviewParams } from '../core/resources/shippingRates';
export type {
    Payment,
    PaymentResponse,
    PaymentOptions,
    CardPaymentMethod,
    BasisTheoryInstance,
    GooglePayToken,
    ApplePayToken,
    PaymentInstrumentInput,
    PaymentInstrumentResponse,
    PaymentInstrumentCustomerResponse,
} from '../core/resources/payments';

// Re-export Store Config (for standalone resolvers / builders)
export { StoreConfigResource } from '../core/resources/storeConfig';
export type { StoreConfig } from '../core/resources/storeConfig';

// Re-export Geo Resource (for standalone geolocation / country detection)
export { GeoResource } from '../core/resources/geo';
export type { GeoLocationData } from '../core/resources/geo';

// Re-export 3DS Resource (for standalone resolvers / builders)
export { ThreedsResource } from '../core/resources/threeds';

// Re-export ISO Data Core (for standalone address form / resolvers)
export { ISODataCore } from '../core/isoData';
export type { ISOCountry, ISORegion } from '../core/isoData';

// Re-export Google Autocomplete Core (for standalone address form)
export { GoogleAutocompleteCore } from '../core/googleAutocomplete';
export type { GooglePrediction, GooglePlaceDetails, AddressComponents } from '../core/googleAutocomplete';

// Re-export Error Types
export {
    TagadaError,
    TagadaApiError,
    TagadaNetworkError,
    TagadaAuthError,
    TagadaValidationError,
    TagadaCircuitBreakerError,
    TagadaErrorCode,
} from '../core/errors';
export type { TagadaErrorOptions, TagadaErrorCodeValue } from '../core/errors';

// Re-export Types
export type { TagadaClientConfig, TagadaState };
export { FunnelActionType } from '../core/resources/funnel';
export type { FunnelAction, FunnelNavigationResult, SimpleFunnelContext } from '../core/resources/funnel';

// Re-export Checkout Types (for external builders)
export type {
    CheckoutData,
    CheckoutSession,
    CheckoutSummary,
    CheckoutSummaryItem,
    CheckoutLineItem,
    CheckoutInitParams,
    Promotion,
} from '../core/resources/checkout';

// Re-export Core Types (domain models)
export type {
    Order,
    OrderItem,
    OrderAddress,
    OrderSummary,
    OrderWithRelations,
    PaymentSummary,
    PromotionSummary,
    Subscription,
    DeviceInfo,
    PromotionCode,
    Customer,
    Store,
    PickupPoint,
} from '../core/types';

// Re-export Utilities (includes formatMoney from core/utils/currency)
export * from '../core/utils';

// Re-export path remapping utilities (for SPA routing)
export {
    matchRoute,
    getInternalPath,
    isPathRemapped,
    getPathInfo,
} from '../core/pathRemapping';

// Re-export Step Config / Payment Flow helpers (for standalone resolvers / builders)
export { getAssignedPaymentFlowId };
export { getAssignedStepConfig } from '../core/funnelClient';
export type { RuntimeStepConfig } from '../core/funnelClient';

// Re-export Payment Service (standalone payment processing — no React)
export { PaymentService } from './payment-service';
export type {
    PaymentServiceConfig,
    PaymentResult,
    PaymentCallbacks,
    CardData,
    ApmData,
} from './payment-service';

// Re-export Apple Pay Service (standalone native Apple Pay — no React)
export { isApplePayAvailable, startApplePaySession } from './apple-pay-service';
export type {
    ApplePayServiceConfig,
    ApplePayPaymentRequest,
    ApplePayTokenResult,
    ApplePayShippingContact,
    ApplePayCallbacks,
} from './apple-pay-service';

// Re-export Google Pay Service (standalone native Google Pay — no React)
export { isGooglePayAvailable, startGooglePaySession } from './google-pay-service';
export type {
    GooglePayServiceConfig,
    GooglePayPaymentRequest,
    GooglePayTokenResult,
    GooglePayAddress,
    GooglePayCallbacks,
} from './google-pay-service';

/**
 * Get BasisTheory public API key based on hostname detection.
 * Delegates to the centralized isProductionBasisTheory() logic.
 */
export function getBasisTheoryApiKey(): string {
    return _getBtApiKey();
}

/**
 * Get BasisTheory tenant ID based on hostname detection.
 */
export function getBasisTheoryTenantId(): string {
    return _getBtTenantId();
}

// ============================================================================
// EXTERNAL PAGE TRACKER
// ============================================================================
// Lightweight tracker for external pages not hosted on Tagadapay
// NOTE: This is also available as a standalone CDN bundle (external-tracker.min.js)
export {
    TagadaTracker,
    TagadaExternalTracker,
} from './external-tracker';

export type {
    TagadaTrackerConfig,
    ExternalTrackerSession,
    NavigateOptions,
} from './external-tracker';
